import React, { useEffect, useState } from "react"
import { Analytics } from "../../Apis/analyticsApi"
import { getFeatureLabelIfKnown } from "../../utils/organizationApiHelpers"

/** Normalize country-rollup feature keys (e.g. Expert-Design, expert design → expert_design). */
const normalizeRollupFeatureKey = (key) =>
  String(key ?? "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_")
    .replace(/\s+/g, "_")

/** Drop duplicate/legacy keys; country rollup uses only snake_case expert_design from API. */
const isExcludedRollupFeatureKey = (feature) => {
  const raw = String(feature ?? "")
  const nk = normalizeRollupFeatureKey(feature)
  if (nk === "event_booth") return true
  /* Same stats bucket as expert_design but wrong key (e.g. expert-design with hyphen). */
  if (nk === "expert_design" && raw.includes("-")) return true
  return false
}

/** API name_en/name_ar overrides when key alone is not enough. */
const FEATURE_LABEL_OVERRIDES = {
  virtual_unstaging: { en: "Virtual unstaging", ar: "إلغاء التجهيز الافتراضي" },
}

/** So very small shares (e.g. 0.18%) stay visible and hoverable in stacked bars. */
const MIN_FEATURE_SEGMENT_PCT = 0.9

const Insightsbycountry = () => {
  const lang = localStorage.getItem("language") || "ar"

  const [countryData, setCountryData] = useState({})
  const [totalCountry, setTotalCountry] = useState({})
  const [loading, setLoading] = useState(false)
  const [tooltip, setTooltip] = useState({
    visible: false,
    name: "",
    count: 0,
    percentage: "",
    x: 0,
    y: 0,
  })

  const showTooltip = (e, name, count, percentage) => {
    const rect = e.target.getBoundingClientRect()
    const centerX = rect.left + rect.width / 2

    setTooltip({
      visible: true,
      name: name,
      count: count,
      percentage: percentage,
      x: centerX,
      y: rect.top,
    })
  }

  const hideTooltip = () => {
    setTooltip((prev) => ({ ...prev, visible: false }))
  }

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true)
      try {
        const response = await Analytics.getCountryRollup()
        if (response.status === 200 && response.data?.countries) {
          setCountryData(response.data.countries)
          setTotalCountry(response.data)
        }
      } catch (error) {
        console.error("Error fetching country rollup:", error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getCountryList = () =>
    Object.entries(countryData).map(([code, data]) => ({
      code,
      users: data.users?.count || 0,
      userPercent: data.users?.percentage?.toFixed(1) || 0,
      features: data.features || {},
      plans: data.plans || {},
    }))

  const usageColors = [
    "#93b5f5",
    "#5a8ff2",
    "#2563eb",
    "#1e3a8a",
    "#60a5fa",
    "#3b82f6",
  ]
  const planColors = ["#8b5cf6", "#10b981", "#0891b2", "#f97316"]

  return (
    <div
      className="insightsbc-container"
      style={{ direction: lang === "ar" ? "rtl" : "ltr" }}
    >
      {loading ? (
        <p style={{ textAlign: "center", padding: "40px 0" }}>
          {lang === "ar" ? "جارٍ تحميل البيانات..." : "Loading data..."}
        </p>
      ) : (
        <>
          {/* USERS BY COUNTRY */}
          <div className="insightsbc-card" style={{ marginBottom: "20px" }}>
            <div className="insightsbc-card-country">
              <div>
                <h3 className="insightsbc-title">
                  {lang === "ar" ? "المستخدمون حسب البلد" : "Users by country"}
                </h3>
              </div>
              <div>
                <span className="country-span">Countries </span>
                <span className="country-count-bold">
                  ({totalCountry?.total_countries})
                </span>
              </div>
            </div>

            {getCountryList().map((c, index) => (
              <div className="insightsbc-row" key={index}>
                <div className="insightsbc-country">
                  <span className="insightsbc-rank">
                    {String(index + 1).padStart(2, "0")}
                  </span>
                  <img
                    src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`}
                    alt={c.code}
                    className="insightsbc-flag"
                    onError={(e) =>
                      (e.target.src = "https://flagcdn.com/w40/xx.png")
                    }
                  />
                  <span className="insightsbc-name">
                    {lang === "ar"
                      ? c.code === "SA"
                        ? "السعودية"
                        : c.code === "PK"
                        ? "باكستان"
                        : c.code === "EG"
                        ? "مصر"
                        : c.code
                      : c.code}
                  </span>
                </div>
                <div className="insightsbc-stats">
                  <p className="insightsbc-users">{c.users}</p>
                  <p className="insightsbc-percent">{c.userPercent}%</p>
                </div>
              </div>
            ))}
          </div>

          {/* FEATURE USAGE */}
          <div className="insightsbc-card">
            <div className="insightsbc-card-country">
              <div>
                <h3 className="insightsbc-title">
                  {lang === "ar"
                    ? "استخدام الميزات حسب البلد"
                    : "Feature usage by country"}
                </h3>
              </div>
              <div>
                <span className="country-span">Countries </span>
                <span className="country-count-bold">
                  ({totalCountry?.total_countries})
                </span>
              </div>
            </div>

            {getCountryList().map((c, index) => {
              const featureEntries = Object.entries(c.features || {}).filter(
                ([feature, obj]) =>
                  obj &&
                  obj.count > 0 &&
                  feature !== "virtual_unstaging" &&
                  !isExcludedRollupFeatureKey(feature)
              )
              const totalPercentage = featureEntries.reduce(
                (sum, [, obj]) => sum + (obj.percentage || 0),
                0
              )
              const normalizationFactor =
                totalPercentage > 0 ? 100 / totalPercentage : 0

              const rawWidths = featureEntries.map(
                ([, obj]) => (obj.percentage || 0) * normalizationFactor
              )
              const boostedWidths = rawWidths.map((w, idx) => {
                const [, obj] = featureEntries[idx]
                const hasUse = obj.count > 0 && w > 0
                return hasUse && w < MIN_FEATURE_SEGMENT_PCT
                  ? MIN_FEATURE_SEGMENT_PCT
                  : w
              })
              const boostedSum = boostedWidths.reduce((a, b) => a + b, 0)
              const displayWidths =
                boostedSum > 0
                  ? boostedWidths.map((w) => (w / boostedSum) * 100)
                  : boostedWidths

              const featureDisplayName = (featureKey, obj) => {
                const nk = normalizeRollupFeatureKey(featureKey)
                /* expert_design payload uses name_ar (e.g. بوث الفعاليات); EN label is Event Booth. */
                if (nk === "expert_design") {
                  return lang === "ar"
                    ? obj.name_ar || "بوث الفعاليات"
                    : "Event Booth"
                }
                const knownLabel = getFeatureLabelIfKnown(
                  nk,
                  lang === "ar" ? "ar" : "en"
                )
                if (knownLabel) return knownLabel
                const override = FEATURE_LABEL_OVERRIDES[nk]
                if (override) {
                  return lang === "ar" ? override.ar : override.en
                }
                return lang === "ar"
                  ? obj.name_ar || featureKey
                  : obj.name_en || featureKey
              }

              return (
                <div className="insightsbc-row" key={index}>
                  <div className="insightsbc-country">
                    <span className="insightsbc-rank">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <img
                      src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`}
                      alt={c.code}
                      className="insightsbc-flag"
                    />
                    <span className="insightsbc-name">
                      {lang === "ar"
                        ? c.code === "SA"
                          ? "السعودية"
                          : c.code === "PK"
                          ? "باكستان"
                          : c.code === "EG"
                          ? "مصر"
                          : c.code
                        : c.code}
                    </span>
                  </div>

                  {/* Feature bars */}
                  <div
                    className="insightsbc-bar-container"
                    style={{
                      position: "relative",
                      width: "100%",
                      borderRadius: "6px",
                      overflow: "hidden",
                      height: "25px",
                    }}
                  >
                    {
                      featureEntries.reduce(
                        (acc, [feature, obj], i) => {
                          const prevWidth = acc.total
                          const adjustedWidth = displayWidths[i] ?? 0
                          acc.total += adjustedWidth

                          const displayName = featureDisplayName(feature, obj)
                          const pctLabel = (Number(obj.percentage) || 0).toFixed(
                            1
                          )

                          acc.bars.push(
                            <div
                              key={i}
                              style={{
                                position: "absolute",
                                left: `${prevWidth}%`,
                                top: 0,
                                height: "100%",
                                width: `${adjustedWidth}%`,
                                backgroundColor:
                                  usageColors[i % usageColors.length] || "#ccc",
                                transition: "width 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) =>
                                showTooltip(
                                  e,
                                  displayName,
                                  obj.count,
                                  pctLabel
                                )
                              }
                              onMouseLeave={hideTooltip}
                            />
                          )
                          return acc
                        },
                        { total: 0, bars: [] }
                      ).bars
                    }
                  </div>
                </div>
              )
            })}
          </div>

          {/* PLANS */}
          <div className="insightsbc-card">
            <div className="insightsbc-card-country">
              <div>
                <h3 className="insightsbc-title">
                  {lang === "ar"
                    ? "الباقات المشتراة حسب البلد"
                    : "Plans bought by country"}
                </h3>
              </div>
              <div>
                <span className="country-span">Countries </span>
                <span className="country-count-bold">
                  ({totalCountry?.total_countries})
                </span>
              </div>
            </div>

            {getCountryList().map((c, index) => {
              const planEntries = Object.entries(c.plans).filter(
                ([, obj]) => obj.count > 0
              )
              const totalPercentage = planEntries.reduce(
                (sum, [, obj]) => sum + (obj.percentage || 0),
                0
              )
              const normalizationFactor =
                totalPercentage > 0 ? 100 / totalPercentage : 0

              return (
                <div className="insightsbc-row" key={index}>
                  <div className="insightsbc-country">
                    <span className="insightsbc-rank">
                      {String(index + 1).padStart(2, "0")}
                    </span>
                    <img
                      src={`https://flagcdn.com/w40/${c.code.toLowerCase()}.png`}
                      alt={c.code}
                      className="insightsbc-flag"
                    />
                    <span className="insightsbc-name">
                      {lang === "ar"
                        ? c.code === "SA"
                          ? "السعودية"
                          : c.code === "PK"
                          ? "باكستان"
                          : c.code === "EG"
                          ? "مصر"
                          : c.code
                        : c.code}
                    </span>
                  </div>

                  {/* Plan bars */}
                  <div
                    className="insightsbc-bar-container"
                    style={{
                      position: "relative",
                      width: "100%",
                      borderRadius: "6px",
                      overflow: "hidden",
                      height: "25px",
                    }}
                  >
                    {
                      planEntries.reduce(
                        (acc, [plan, obj], i) => {
                          const prevWidth = acc.total
                          const adjustedWidth =
                            (obj.percentage || 0) * normalizationFactor
                          acc.total += adjustedWidth

                          const displayPlan =
                            lang === "ar"
                              ? obj.plan_ar || plan
                              : obj.plan_en || plan

                          acc.bars.push(
                            <div
                              key={i}
                              style={{
                                position: "absolute",
                                left: `${prevWidth}%`,
                                top: 0,
                                height: "100%",
                                width: `${adjustedWidth}%`,
                                backgroundColor:
                                  planColors[i % planColors.length] || "#ccc",
                                transition: "width 0.3s ease",
                                cursor: "pointer",
                              }}
                              onMouseEnter={(e) =>
                                showTooltip(
                                  e,
                                  displayPlan,
                                  obj.count,
                                  obj.percentage.toFixed(1)
                                )
                              }
                              onMouseLeave={hideTooltip}
                            />
                          )
                          return acc
                        },
                        { total: 0, bars: [] }
                      ).bars
                    }
                  </div>
                </div>
              )
            })}
          </div>

          {/* TOOLTIP */}
          {tooltip.visible && (
            <div
              style={{
                position: "fixed",
                left: tooltip.x,
                top: tooltip.y,
                transform: "translate(-50%, calc(-100% - 15px))",
                background: "#FFFFFF",
                padding: "12px 16px",
                borderRadius: "8px",
                pointerEvents: "none",
                zIndex: 10000,
                boxShadow: "0px 4px 16px rgba(0, 0, 0, 0.12)",
                minWidth: "160px",
                textAlign: lang === "ar" ? "right" : "left",
                direction: lang === "ar" ? "rtl" : "ltr",
              }}
            >
              <div
                style={{
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#6B7280",
                  marginBottom: "4px",
                  textTransform: "none",
                }}
              >
                {tooltip.name}
              </div>

              <div
                style={{
                  fontSize: "16px",
                  fontWeight: "500",
                  color: "#111827",
                  letterSpacing: "-0.01em",
                }}
              >
                {lang === "ar"
                  ? `${tooltip.count.toLocaleString()} | ${tooltip.percentage}%`
                  : `${tooltip.count.toLocaleString()} | ${
                      tooltip.percentage
                    }%`}
              </div>

              <div
                style={{
                  position: "absolute",
                  bottom: "-8px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: 0,
                  height: 0,
                  borderLeft: "8px solid transparent",
                  borderRight: "8px solid transparent",
                  borderTop: "8px solid #FFFFFF",
                  filter: "drop-shadow(0px 2px 4px rgba(0, 0, 0, 0.08))",
                }}
              />
            </div>
          )}
        </>
      )}
    </div>
  )
}

export default Insightsbycountry
